import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'
import { Subscription } from 'effector'

import { BehaviorSubject, Observable, switchMap } from 'rxjs'
import { map, tap } from 'rxjs/operators'

import { BpmnGraphUtil } from '@flowx/designer/modules/bpmn-editor/bpmn-graph.util'
import { BpmnEditorEvents } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.events'
import { BpmnEditorStore } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.store'
import { NodeDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { NodeDefinitionNormalized } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ShapeLayoutOptions } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import { BulkUpdateProcessEntitiesPayload } from '@flowx/designer/shared/entities/process-api.types'
import { ProcessDefinitionService } from '@flowx/designer/shared/services/process-definition.service'
import {
  ProcessApiParameters,
  UrlParametersStoreService,
} from '@flowx/designer/shared/services/url-parameters-store.service'

import { AiAnalystUtils } from '../ai/agents/analyst/services/analyst.service'
import { AiActions } from '../ai/store/ai.actions'
import { AiProcessEntities } from '../ai/types/ai.types'

/** AI draft preview shown on the React Flow canvas */
export interface AiDraftPreviewState {
  entities: AiProcessEntities
  /** Pre-existing node ids, rendered dimmed */
  disabledNodeIds: NodeDefinition['id'][]
}

@Injectable({
  providedIn: 'root',
})
export class BpmnAiAnalystService {
  #processDefinitionService = inject(ProcessDefinitionService)
  #store = inject(Store)
  #bpmnEditorStore = inject(BpmnEditorStore)

  #updatePreviewOutlineSubscription: Subscription = null
  #urlParametersStore = inject(UrlParametersStoreService)

  /** Draft preview consumed by the React Flow host component; null outside draft mode */
  #aiDraftPreview = new BehaviorSubject<AiDraftPreviewState | null>(null)
  readonly aiDraftPreview$: Observable<AiDraftPreviewState | null> =
    this.#aiDraftPreview.asObservable()

  renderAiProcessEntities(aiEntities: AiProcessEntities): void {
    if (aiEntities) {
      const processStateSnapshot = AiAnalystUtils.getProcessStateSnapshot()
      this.#drawDraftPreview({
        aiEntities,
        oldNodes: processStateSnapshot.nodes,
      })
      this.#enableDraftModeInEffector(aiEntities)
    }
  }

  discardDraft(): void {
    this.#transitionFromDraftToEditMode()
  }

  keepDraft(aiEntities: AiProcessEntities): Observable<any> {
    return this.#saveProcessUpdates(aiEntities.updates).pipe(
      tap(() => this.#transitionFromDraftToEditMode())
    )
  }

  /**
   * Saves the process updates to the BE using the bulkUpdateProcessEntities.
   * When the save is successful, it reloads the process.
   *
   * The assumption is that the function used to reload the process also updates
   * the FE store state as a **side effect**.
   *
   * @param updates
   * The updates object containing the nodes, sequences, boundary events and swimlanes to save.
   *
   * @returns
   * The original BE response from the bulkUpdateProcessEntities call.
   */
  #saveProcessUpdates(updates: BulkUpdateProcessEntitiesPayload): Observable<any> {
    const processApiParameters: ProcessApiParameters = this.#urlParametersStore.urlParameters
    return this.#processDefinitionService.bulkUpdateProcessEntities(updates).pipe(
      // TODO: This is a temporary implementation
      // The nicer way would be to surgically update the FE state based on the created
      // entities returned by the API response & the updates stored in the service
      switchMap((bulkUpdateProcessEntitiesResponse) =>
        this.#processDefinitionService
          .getApplicationProcess(processApiParameters)
          .pipe(map(() => bulkUpdateProcessEntitiesResponse))
      )
    )
  }

  #closePreviewOutlineWatch(): void {
    this.#updatePreviewOutlineSubscription?.unsubscribe()
    this.#updatePreviewOutlineSubscription = null
  }

  #transitionFromDraftToEditMode(): void {
    this.#aiDraftPreview.next(null)
    this.#closePreviewOutlineWatch()
    // Some things are still calculated based on the effector ai store state,
    // so we need to clear that state.
    BpmnEditorEvents.updatedDraft({ aiNodes: 0, aiSequences: 0 })
  }

  /**
   * Publishes the draft for the host component — it swaps the diagram's data to the
   * merged draft entities, dims the pre-existing nodes, renders the sequences in
   * preview style and draws the dashed outline.
   */
  #drawDraftPreview(payload: {
    aiEntities: AiProcessEntities
    oldNodes: Map<NodeDefinition['id'], NodeDefinitionNormalized>
  }): void {
    const { aiEntities, oldNodes } = payload
    this.#aiDraftPreview.next({
      entities: aiEntities,
      disabledNodeIds: [...oldNodes.keys()],
    })

    const previewOutlineLayoutOptions = aiEntities.previewOutline
    // A re-generated draft replaces the previous one — drop the old watcher so it
    // stops dispatching the stale outline's transform.
    this.#closePreviewOutlineWatch()
    // Fires immediately with the current transform and again on every RF
    // pan/zoom — keeps the "Generated Process" header glued to the outline.
    this.#updatePreviewOutlineSubscription = this.#bpmnEditorStore.canvasTransformation$.watch(() =>
      this.#updatePreviewOutline(previewOutlineLayoutOptions)
    )
  }

  #enableDraftModeInEffector(aiEntities: AiProcessEntities): void {
    const payload = {
      aiNodes:
        Object.keys(aiEntities.updates.nodes.added).length +
        aiEntities.updates.nodes.deleted.length,
      aiSequences:
        aiEntities.updates.sequences.added.length + aiEntities.updates.sequences.deleted.length,
    }
    BpmnEditorEvents.updatedDraft(payload)
  }

  #updatePreviewOutline(layoutOptions: ShapeLayoutOptions): void {
    // The canvas mirrors its viewport into the effector stores
    // (canvasViewportTransform / canvasZoom).
    const view = {
      zoom: this.#bpmnEditorStore.canvasZoom$.getState().zoom,
      viewportTransform: this.#bpmnEditorStore.canvasTransformation$.getState(),
    }
    if (!view.viewportTransform) {
      return
    }
    const aiPreviewBoxOptions = this.#calculatePreviewOutlineTransform({
      ...view,
      layoutOptions,
    })
    this.#store.dispatch(
      AiActions.canvasTransform({ generatedProcessPreviewHeader: aiPreviewBoxOptions })
    )
  }

  #calculatePreviewOutlineTransform(payload: {
    zoom: number
    viewportTransform: number[]
    layoutOptions: ShapeLayoutOptions
  }): {
    containerTransform: string
    contentTransform: string
  } {
    const { zoom, viewportTransform, layoutOptions } = payload
    const { x, y } = layoutOptions
    const anchorPoint = BpmnGraphUtil.transformPoint({ x, y }, viewportTransform)

    return {
      containerTransform: `translate(${anchorPoint.x}px, ${anchorPoint.y}px)`,
      contentTransform: `scale(${zoom})`,
    }
  }
}
